import { Dispatch } from 'react';

import { getAssociatedServices, getCompensatoryEventMapDetails } from '../../../../store/_api';
import { AssociatedService, IngressEvent, Service, ServiceOperation } from '../../../../store/_api/TypeDefs';
import { FlowBuilderElement } from '../../../../_components/flow-builder/FlowBuilder';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { FlowButton, FlowConnector, FlowItem, FlowItemConfig } from '../../../../_components/flow-builder/TypeDefs';
import { colors } from '../../../../constants/FlowBuilder';
import { FLOW_CONFIG, journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { Actions, UpdateActions } from '../../reducer/TypeDefs';
import { DraggingItemType, FlowItemButtonActions, FlowItemConditions, FlowItemDataMap, LoaderHook, SelectedFlowItem, ToastMessageHook, TranslateHook } from '../TypeDefs';
import { createCEM } from './CemHelpers';
import { translateLabel } from './PrepareJourneyHelpers';

import createCem from '../../../../assets/images/CreateCem.svg';
import deleteItemIcon from '../../../../assets/images/DeleteCanvasElement.svg';
import editCem from '../../../../assets/images/EditCem.svg';
import transformPayloadIcon from '../../../../assets/images/TransformPayload.svg';
import { computeIsCompensateAction } from './CompensationHelpers';

/**
 * @method getFlowItemConfig
 * @description change config when condition text is changed or node is external
 * @param {boolean} isExternal - Indicates if the node is external.
 * @param {string} conditionText - The condition text (AND/OR).
 * @param {FlowItemConfig} config - The canvas config.
 * @returns config
 */
export const getFlowItemConfig = ({
  isExternal,
  conditionText,
  config = {}
}: {
  isExternal?: boolean,
  conditionText?: string,
  config?: FlowItemConfig
}): FlowItemConfig => {
  if (isExternal === true) {
    config.selectedStrokeColor = colors.secondary;
    config.selectedFillColor = colors.secondary;
  }
  if (conditionText === FlowItemConditions.AND || conditionText === FlowItemConditions.T_AND) {
    config.conditionTextBgColor = colors.white;
    config.conditionTextFgColor = colors.conditionText;
  } else {
    config.conditionTextBgColor = colors.conditionText;
    config.conditionTextFgColor = colors.white;
  }

  return config;
};

/**
 * @method getFlowItemButtons
 * @description show specific buttons when hovered on nodes
 * @param {boolean} hasCEM - Indicates if the node has compensatory event map.
 * @param {boolean} isCompensateAction - Indicates if the node is able to create compensatory event map.
 * @param {boolean} transformPayload - Indicates if the payload can be transformed.
 * @param {boolean} readOnly - if canvas is in view mode
 * @returns {FlowButton[]} - The buttons to be shown when hovered on nodes.
 */
export const getFlowItemButtons = (
  hasCEM: boolean,
  isCompensateAction: boolean,
  transformPayload: boolean,
  readOnly: boolean
): FlowButton[] => {
  const btns: FlowButton[] = [];
  if (transformPayload) {
    btns.push({
      icon: transformPayloadIcon,
      id: FlowItemButtonActions.TRANSFORM_PAYLOAD,
      label: translateLabel('labels.transform_payload'),
      height: journeyCanvasConstants.flowItemBtnHeight,
      width: journeyCanvasConstants.flowItemBtnHeight,
    });
  }
  if (hasCEM) {
    if (readOnly) {
      btns.push({
        icon: createCem,
        id: FlowItemButtonActions.EDIT_CEM,
        label: translateLabel('labels.view_cem'),
        height: journeyCanvasConstants.flowItemBtnHeight,
        width: journeyCanvasConstants.flowItemBtnHeight * (17 / 11),
      });
    } else {
      btns.push({
        icon: editCem,
        id: FlowItemButtonActions.EDIT_CEM,
        label: translateLabel('labels.edit_cem'),
        height: journeyCanvasConstants.flowItemBtnHeight,
        width: journeyCanvasConstants.flowItemBtnHeight * 2,
      });
    }
  } else if (isCompensateAction && !readOnly) {
    btns.push({
      icon: createCem,
      id: FlowItemButtonActions.CREATE_CEM,
      label: translateLabel('labels.create_cem'),
      height: journeyCanvasConstants.flowItemBtnHeight,
      width: journeyCanvasConstants.flowItemBtnHeight * (17 / 11), // very specific based on svg size
    });
  }
  if (!readOnly) {
    btns.push({
      icon: deleteItemIcon,
      id: FlowItemButtonActions.DELETE_ITEM,
      label: translateLabel('labels.delete'),
      height: journeyCanvasConstants.flowItemBtnHeight,
      width: journeyCanvasConstants.flowItemBtnHeight,
    });
  }
  return btns;
};

/**
 * @method flowItemDragCompleteHandler
 * @description dispatching reducer function to complete drag for a flow item on canvas
 * @param {string} nodeId - The ID of the flow item.
 * @param {number} x - The x-coordinate of the dropped position.
 * @param {number} y - The y-coordinate of the dropped position.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const flowItemDragCompleteHandler = (nodeId: string, x: number, y: number, dispatch: Dispatch<UpdateActions>) => {
  dispatch({
    type: Actions.FLOW_ITEM_DRAG_COMPLETE,
    payload: {
      nodeId,
      x,
      y
    }
  });
};

/**
 * @method deleteItem
 * @description deleting the item on canvas
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {string | undefined} deletingNodeId - The ID of the node to be deleted.
 */
export const deleteItem = (dispatch: Dispatch<UpdateActions>, deletingNodeId: string | undefined) => {
  dispatch({ type: Actions.DELETE_FLOW_ITEM, payload: { nodeId: deletingNodeId ?? '' } });
};

/**
 * @method deleteItemAndRefreshAssociatedServices
 * @description deleting the item on canvas and refresh associated services for CEM
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {string | undefined} deletingNodeId - The ID of the node to be deleted.
 * @param {FlowItemDataMap} flowItemDataMap - The flow item data map.
 * @param {string | undefined} cemRootNodeId - The CEM root node ID.
 * @param {boolean} designingCEM - Whether we're in CEM mode.
 */
export const deleteItemAndRefreshAssociatedServices = async (
  dispatch: Dispatch<UpdateActions>,
  deletingNodeId: string | undefined,
  flowItemDataMap: FlowItemDataMap,
  cemRootNodeId: string | undefined,
  designingCEM: boolean
) => {
  // First delete the item
  dispatch({ type: Actions.DELETE_FLOW_ITEM, payload: { nodeId: deletingNodeId ?? '' } });

  // Then refresh associated services if we're in CEM mode
  if (designingCEM && cemRootNodeId && flowItemDataMap[cemRootNodeId]) {
    const cemRootItem = flowItemDataMap[cemRootNodeId];
    if (cemRootItem.ingressEvent) {
      // Set loading state
      dispatch({ type: Actions.SIDEBAR_DATA_LOADING });

      try {
        const associatedServices = await getAssociatedServices(
          cemRootItem.serviceVersion,
          cemRootItem.serviceName,
          cemRootItem.ingressEvent,
          cemRootItem.ingressEventVersion,
          cemRootItem.serviceOperationName
        );

        if (associatedServices) {
          dispatch({ type: Actions.FETCH_ALL_ASSOCIATED_SERVICES, payload: { services: associatedServices } });
        } else {
          dispatch({ type: Actions.SIDEBAR_DATA_ERROR });
        }
      } catch (error) {
        dispatch({ type: Actions.SIDEBAR_DATA_ERROR });
      }
    }
  }
};

/**
 * @method dragStartHandler
 * @description dragging the service on the canvas
 * @param {Service | AssociatedService} item - The dragged item.
 * @param {DraggingItemType} itemType - The type of the dragged item.
 * @param {React.DragEvent} ev - The drag event.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const dragStartHandler = (item: Service | AssociatedService, itemType: DraggingItemType, ev: React.DragEvent, dispatch: Dispatch<UpdateActions>, serviceOperation?: ServiceOperation, ingressEvent?: IngressEvent) => {
  if (ev.target instanceof HTMLElement) {
    const { left, top } = ev.target.getBoundingClientRect();
    let { clientX, clientY } = ev;
    clientX = clientX ?? 0;
    clientY = clientY ?? 0;
    // remember the offset when drag starts so that, offset can be used when placing the item on canvas
    dispatch({
      type: Actions.DRAGGING_ITEM,
      payload: {
        item,
        serviceOperation,
        ingressEvent,
        startOffset: {
          x: (clientX - left) / (ev.target.offsetWidth || 1),
          y: (clientY - top) / (ev.target.offsetHeight || 1),
        },
        itemType
      },
    });
  }
};

/**
 * @method itemButtonClickHandler
 * @description Handles the icon buttons on top of flow item.
 * @param {string} id - The ID of the clicked button.
 * @param {string} nodeId - The ID of the flow item.
 * @param {LoaderHook} loader - The loader hook.
 * @param {ToastMessageHook} toastMessage - The toast message hook.
 * @param {TranslateHook} translate - The translate hook.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {FlowItemDataMap} flowItemDataMap - The map of flow item data.
 * @param {boolean} readOnly - if canvas is in view mode
 */
export const itemButtonClickHandler = async (
  {
    id,
    nodeId,
    loader,
    toastMessage,
    translate,
    dispatch,
    flowItemDataMap,
    journeyVersion,
    readOnly
  }: {
    id: string,
    nodeId: string,
    loader: LoaderHook,
    toastMessage: ToastMessageHook,
    translate: TranslateHook,
    dispatch: Dispatch<UpdateActions>,
    flowItemDataMap: FlowItemDataMap,
    journeyVersion: number,
    readOnly: boolean,
  }
) => {
  const cemEventName = flowItemDataMap[nodeId].compensatoryEventMapName;
  if (id === FlowItemButtonActions.CREATE_CEM || id === FlowItemButtonActions.EDIT_CEM) {
    dispatch({ type: Actions.CLEAR_EVENT_SCHEMA });
    dispatch({ type: Actions.HIDE_COND_EXP_MODAL });
    if (cemEventName) {
      loader.show();
      const resp = await getCompensatoryEventMapDetails(cemEventName, journeyVersion);
      if (!resp) {
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_load_cem_failed'));
        loader.hide();
        return;
      }
      const compensatoryEventMap = createCEM({
        node: resp,
        itemEventMap: flowItemDataMap,
        nodeId,
        readOnly
      });
      loader.hide();
      dispatch({ type: Actions.DESIGN_COMPENSATORY_EVENT_MAP, payload: { parentNodeId: nodeId, compensatoryEventMap } });
    } else {
      // Fetch associated services for the parent node when creating new CEM
      const selectedItem = flowItemDataMap[nodeId];
      if (selectedItem?.ingressEvent) {
        dispatch({ type: Actions.SIDEBAR_DATA_LOADING });
        (async () => {
          const associatedServices = await getAssociatedServices(selectedItem.serviceVersion, selectedItem.serviceName, selectedItem.ingressEvent, selectedItem.ingressEventVersion, selectedItem.serviceOperationName);
          if (associatedServices) {
            dispatch({ type: Actions.FETCH_ALL_ASSOCIATED_SERVICES, payload: { services: associatedServices } });
          } else {
            dispatch({ type: Actions.SIDEBAR_DATA_ERROR });
          }
        })();
      }
      dispatch({ type: Actions.DESIGN_COMPENSATORY_EVENT_MAP, payload: { parentNodeId: nodeId } });
    }
  } else if (id === FlowItemButtonActions.TRANSFORM_PAYLOAD) {
    dispatch({ type: Actions.OPEN_PAYLOAD_TRANSFORM_MODAL, payload: { nodeId } });
  } else if (id === FlowItemButtonActions.DELETE_ITEM) {
    dispatch({ type: Actions.NOTIFY_DELETE_NODE, payload: { nodeId } });
  }
};

/**
 * Fetches the associated services for a flow item and dispatches the appropriate
 * sidebar-loading / sidebar-error / fetched-services actions. Used both by the normal click
 * handler and by the post-Control-Plane resume effect so the two stay in sync.
 */
export const loadAssociatedServicesForNode = (
  dispatch: Dispatch<UpdateActions>,
  flowItemDataMap: FlowItemDataMap,
  nodeId: string,
  cemRootNodeId: string | undefined
): Promise<void> => {
  const selectedItem = flowItemDataMap[nodeId];
  if (!selectedItem?.ingressEvent || nodeId === cemRootNodeId) {
    return Promise.resolve();
  }
  dispatch({ type: Actions.SIDEBAR_DATA_LOADING });
  return (async () => {
    const associatedServices = await getAssociatedServices(
      selectedItem.serviceVersion,
      selectedItem.serviceName,
      selectedItem.ingressEvent,
      selectedItem.ingressEventVersion,
      selectedItem.serviceOperationName
    );
    if (associatedServices) {
      dispatch({ type: Actions.FETCH_ALL_ASSOCIATED_SERVICES, payload: { services: associatedServices } });
    } else {
      dispatch({ type: Actions.SIDEBAR_DATA_ERROR });
    }
  })();
};

/**
 * @method elementSelectHandler
 * @description Handles the selection of an element.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {FlowItemDataMap} flowItemDataMap - The map of flow item data.
 * @param {boolean} designingStep - Indicates if the step is being designed.
 * @param {string | undefined} cemRootNodeId - The ID of the CEM root node.
 * @param {string | undefined} nodeId - The ID of the selected element (optional).
 */
export const elementSelectHandler = (
  {
    dispatch,
    flowItemDataMap,
    designingStep,
    cemRootNodeId,
    nodeId
  }: {
    dispatch: Dispatch<UpdateActions>,
    flowItemDataMap: FlowItemDataMap,
    designingStep: boolean,
    cemRootNodeId: string | undefined
    nodeId?: string
  }
) => {
  if (nodeId) {
    // an event is selected
    // - store the event object
    // - filter the left pane with egress event/journeys of selected event
    const selectedItem = flowItemDataMap[nodeId];
    if (!designingStep) {
      dispatch({
        type: Actions.CLICK_STEP_CARD,
        payload: {
          nodeId
        }
      });
    } else if (selectedItem) {
      dispatch({
        type: Actions.SELECT_FLOW_ITEM,
        payload: { nodeId, serviceData: selectedItem }
      });
    }

    loadAssociatedServicesForNode(dispatch, flowItemDataMap, nodeId, cemRootNodeId);
  } else {
    dispatch({ type: Actions.NONE_SELECTED });
  }
};

/**
 * @method dropEndHandler
 * @description Handles dropping items on canvas.
 * @param {React.DragEvent} ev - The drop event.
 * @param {HTMLDivElement | null} canvasRoot - The root element of the canvas.
 * @param {FlowBuilderElement | null} flowBuilderRef - The reference to the flow builder element.
 * @param {LoaderHook} loader - The loader hook.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {DraggingItem} draggingItem - The dragging item data.
 * @param {boolean} designingCEM - Indicates if designing CEM.
 * @param {boolean} readOnly - if canvas is in view mode
 */
export const dropEndHandler = (
  {
    ev,
    canvasRoot,
    flowBuilderRef,
    dispatch,
    draggingItem,
    designingCEM,
    readOnly,
  }: {
    ev: React.DragEvent,
    canvasRoot: HTMLDivElement | null,
    flowBuilderRef: FlowBuilderElement | null,
    dispatch: Dispatch<UpdateActions>,
    draggingItem: {
      item: AssociatedService | Service | null;
      serviceOperation?: ServiceOperation;
      ingressEvent?: IngressEvent;
      startOffset: {
        x: number;
        y: number;
      };
      itemType: DraggingItemType;
    },
    designingCEM: boolean,
    readOnly: boolean
  }
) => {
  if (readOnly) {
    return;
  }
  dispatch({
    type: Actions.DRAGGING_ITEM,
    payload: {
      item: null,
      startOffset: {
        x: draggingItem.startOffset.x,
        y: draggingItem.startOffset.y
      },
      itemType: DraggingItemType.NONE
    },
  });
  const { item, itemType, ingressEvent, serviceOperation } = draggingItem;
  if (canvasRoot && item) {
    let { left, top, right, bottom } = canvasRoot.getBoundingClientRect();
    // adjust the bound to get the actual canvas bounds
    left += journeyCanvasConstants.canvasMarginAndBorder;
    top += journeyCanvasConstants.canvasMarginAndBorder;
    right += journeyCanvasConstants.canvasMarginAndBorder;
    bottom += journeyCanvasConstants.canvasMarginAndBorder;

    let { clientX, clientY } = ev;
    clientX = clientX ?? 0;
    clientY = clientY ?? 0;

    // check if the dropped co-ordinates are in canvas bounds
    if (
      clientX >= left
      && clientX <= right
      && clientY >= top
      && clientY <= bottom
    ) {
      const isCompensateAction = computeIsCompensateAction({
        designingCEM,
        serviceType: item.serviceType,
        svcIsCompensateAction: item.isCompensateAction,
        ingressEvent,
      });
      dispatch({
        type: Actions.CREATE_FLOW_ITEM,
        payload: {
          service: item,
          serviceOperation,
          ingressEvent,
          left,
          top,
          client: { x: clientX, y: clientY },
          orientation: FLOW_CONFIG.orientation ?? Orientation.HORIZONTAL,
          flowBuilder: flowBuilderRef,
          type: itemType,
          isCompensateAction: isCompensateAction ?? false
        },
      });
    }
  }
};

/**
 * @method shouldItemBeStartNode
 * @description Checks if the current flow item can be set as the start node.
 * @param {SelectedFlowItem | undefined} selectedFlowItem - The currently selected flow item.
 * @param {FlowConnector[]} flowConnectors - The list of flow connectors.
 * @param {string} prevStepCardUUID - The UUID of the previous step card.
 * @param {FlowItem} flowItem - The current flow item.
 * @param {string} ingressNodeId - The ID of the ingress node.
 * @returns {boolean} - Returns true if the current flow item can be set as the start node, otherwise false.
 */
export const shouldItemBeStartNode = (selectedFlowItem: SelectedFlowItem | undefined, flowConnectors: FlowConnector[], prevStepCardUUID: string, flowItem: FlowItem, ingressNodeId: string) => {
  const existingConnector = selectedFlowItem && flowConnectors.find((connector) => connector.fromNodeId === selectedFlowItem.nodeId);

  // don't allow divergence from subsequent step starting node
  if (selectedFlowItem && prevStepCardUUID && existingConnector && flowItem.nodeId === selectedFlowItem.nodeId) {
    return false;
  }

  // don't allow diverge from starting node if external
  if (selectedFlowItem && selectedFlowItem.nodeId === ingressNodeId && selectedFlowItem.serviceData.isExternal && existingConnector) {
    return false;
  }

  return true;
};

/**
 * @method isRootNode
 * @description Assigns the isRoot to true when first flow item is selected
 * @param {boolean} pickingService - Indicates if a service is being picked.
 * @param {string} prevStepCardUUID - The UUID of the previous step card.
 * @param {SelectedFlowItem | undefined} selectedFlowItem - The currently selected flow item.
 * @param {FlowItem} flowItem - The current flow item.
 * @returns {boolean} - Returns true if the current flow item is the root node, otherwise false.
 */
export const isRootNode = (pickingService: boolean, prevStepCardUUID: string, selectedFlowItem: SelectedFlowItem | undefined, flowItem: FlowItem) => {
  // for first step isRoot in case of picking first service
  let isRoot = pickingService;
  if (prevStepCardUUID && selectedFlowItem) {
    // if this is subsequent step then node connecting from
    if (flowItem.nodeId === selectedFlowItem.nodeId) {
      isRoot = true;
    }
  }
  return isRoot;
};

/**
 * @method shouldConnectVertically
 * @description Checks if the flow item should be displayed vertically.
 * @param {boolean} designingCEM - Indicates if the CEM is being designed.
 * @param {boolean} pickingService - Indicates if a service is being picked.
 * @param {boolean} designingStep - Indicates if a step is being designed.
 * @param {boolean} designingInitialStepJourney - Indicates if the initial step journey is being designed.
 * @param {number} flowItemLength - The length of the flow item.
 * @returns {boolean} - Returns true if the flow item should be displayed vertically, otherwise false.
 */
export const shouldConnectVertically = (designingCEM: boolean, pickingService: boolean, designingStep: boolean, designingInitialStepJourney: boolean, flowItemLength: number) => {
  if ((designingCEM && pickingService) // designing cem OR picking up first service in subsequent step
    || (designingStep && !designingInitialStepJourney && flowItemLength === 1)) {
    return true;
  }

  return false;
};
